'use client';

// Trailing share button for shareable activity rows. Trade activity keeps the
// existing trade permalink + OG payload; Boost activity shares the canonical
// market URL with Boost-specific copy and analytics.
import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
import { LinkForwardIconSolid } from '@/components/ui/icon/glyphs';
import { withDemoMode } from '@/lib/network';
import { useUserNetwork } from '@/lib/network/client';
import { buildTradeShareUrl, openShareModal } from '@/lib/share';
interface TradeActivityShareButtonProps {
  activityType: 'trade';
  /** Trade tx signature — the activity row's stable `id`. */
  signature: string;
  marketPubkey: string;
  /** Wallet that executed the trade (`trades.trader`). */
  traderPubkey: string;
  direction: 'buy' | 'sell';
  amountD6: string;
  outcomeIndex: number;
}
interface BoostActivityShareButtonProps {
  activityType: 'boost';
  marketPubkey: string;
  marketTitle: string;
}
type ActivityShareButtonProps = TradeActivityShareButtonProps | BoostActivityShareButtonProps;
export function ActivityShareButton(props) {
  const $ = _c(12);
  const t = useTranslations("chart");
  const tShare = useTranslations("tx.share");
  const tActivity = useTranslations("profile.stats");
  const {
    network
  } = useUserNetwork();
  let t0;
  if ($[0] !== network || $[1] !== props || $[2] !== tActivity || $[3] !== tShare) {
    t0 = function handleShare() {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      if (origin.length === 0) {
        return;
      }
      const u = buildActivityShareUrl(props, origin);
      withDemoMode(u, network === "mainnet" ? "mainnet" : "devnet");
      if (props.activityType === "trade") {
        openShareModal({
          surface: {
            kind: "trade",
            signature: props.signature,
            marketPubkey: props.marketPubkey,
            userPubkey: props.traderPubkey,
            direction: props.direction,
            amountD6: props.amountD6,
            outcomeIndex: props.outcomeIndex
          },
          url: u.toString(),
          text: tShare("tweetText", {
            direction: props.direction,
            amount: ""
          }),
          title: tShare("modalTitle")
        });
        return;
      }
      openShareModal({
        surface: {
          kind: "boost",
          marketPubkey: props.marketPubkey
        },
        url: u.toString(),
        text: tActivity("activity_share_boost_text", {
          marketTitle: props.marketTitle
        }),
        title: tActivity("activity_share_boost_modal_title")
      });
    };
    $[0] = network;
    $[1] = props;
    $[2] = tActivity;
    $[3] = tShare;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const handleShare = t0;
  let t1;
  if ($[5] !== props.activityType || $[6] !== t || $[7] !== tActivity) {
    t1 = props.activityType === "trade" ? t("trade_action_share") : tActivity("activity_share_boost_aria");
    $[5] = props.activityType;
    $[6] = t;
    $[7] = tActivity;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== handleShare || $[10] !== t1) {
    t2 = <AnimatedButton variant="tertiary" size="sm" prefix={LinkForwardIconSolid} aria-label={t1} onClick={handleShare} className="size-7 shrink-0 p-0" />;
    $[9] = handleShare;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
function buildActivityShareUrl(props: ActivityShareButtonProps, origin: string): URL {
  if (props.activityType === 'boost') {
    return new URL(`/markets/${props.marketPubkey}`, origin);
  }
  return new URL(buildTradeShareUrl(props.signature, {
    marketPubkey: props.marketPubkey,
    userPubkey: props.traderPubkey,
    direction: props.direction,
    amountD6: props.amountD6,
    outcomeIndex: props.outcomeIndex
  }), origin);
}